import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { formatApiError, api } from "../lib/api";
import { Eye, EyeOff, Lock } from "lucide-react";
import PasswordStrengthMeter, { scorePassword } from "../components/PasswordStrengthMeter";
import { CountrySelect, useCountries } from "../components/ProfileFields";

export default function RegisterPage() {
  const { register, googleLogin } = useAuth();
  const nav = useNavigate();
  const countries = useCountries();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "buyer",
    phone: "", phone_country: "", phone_cc: "", country: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // IP-geo default for the country picker
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        const j = await r.json();
        const iso = (j.country_code || "").toUpperCase();
        if (iso) setForm(f => ({
          ...f,
          country: f.country || iso,
          phone_country: f.phone_country || iso,
          phone_cc: f.phone_cc || (j.country_calling_code || ""),
        }));
      } catch (e) {
        // Geo lookup is best-effort; user can pick country manually.
        if (process.env.NODE_ENV !== "production") console.debug("[register] geo:", e?.message);
      }
    })();
  }, []);

  const pwScore = scorePassword(form.password).score;
  const canSubmit = form.password.length >= 8 && pwScore >= 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setErr("Please choose a stronger password (at least 8 characters).");
      return;
    }
    setErr(""); setBusy(true);
    try {
      await register(form);
      nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setPhoneCountry = (iso2) => {
    const c = countries.find(x => x.iso2 === iso2);
    setForm(f => ({ ...f, phone_country: iso2, phone_cc: c?.dial_code || f.phone_cc }));
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="text-xs font-bold uppercase tracking-widest text-[#A1A1AA] hover:text-[#D4FF00]">← Back</Link>
        <h1 className="font-display font-black text-5xl uppercase mt-4 mb-2">join the<br/><span className="title-gradient">drop</span>.</h1>
        <p className="text-[#A1A1AA] text-sm mb-8">Create your free account.</p>

        <button onClick={googleLogin} data-testid="google-register-btn" className="w-full mb-4 py-3 rounded-full bg-white text-black font-bold flex items-center justify-center gap-2 active:scale-95 transition">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9C29 35.7 26.6 36.5 24 36.5c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.7 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6 4.9C41.4 34.9 44 29.8 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 border-t border-[#3D2845]"/>
          <span className="text-xs text-[#A1A1AA] uppercase tracking-widest">or</span>
          <div className="flex-1 border-t border-[#3D2845]"/>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input data-testid="reg-name" required placeholder="Name" className="input-dark" value={form.name} onChange={e=>set("name", e.target.value)}/>
          <input data-testid="reg-email" type="email" required placeholder="Email" className="input-dark" value={form.email} onChange={e=>set("email", e.target.value)}/>
          <div className="relative">
            <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]"/>
            <input
              data-testid="reg-password"
              type={showPw ? "text" : "password"}
              required minLength={8}
              placeholder="Password (min 8 chars)"
              className="input-dark pl-10 pr-12"
              value={form.password}
              onChange={e=>set("password", e.target.value)}
            />
            <button type="button" onClick={()=>setShowPw(!showPw)}
              data-testid="reg-toggle-pw"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#D4FF00] p-1">
              {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
          <PasswordStrengthMeter password={form.password}/>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1">Country</div>
            <CountrySelect value={form.country}
              testid="reg-country"
              onChange={(iso2) => setForm(f => ({
                ...f, country: iso2,
                phone_country: f.phone_country || iso2,
                phone_cc: f.phone_cc || (countries.find(c => c.iso2 === iso2)?.dial_code || ""),
              }))}/>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1">Phone (optional)</div>
            <div className="grid grid-cols-[auto_1fr] gap-2">
              <div className="min-w-[140px]">
                <CountrySelect value={form.phone_country} onChange={setPhoneCountry}
                  testid="reg-phone-cc"/>
              </div>
              <input data-testid="reg-phone" type="tel" className="input-dark"
                placeholder="Phone number"
                value={form.phone} onChange={e => set("phone", e.target.value)}/>
            </div>
          </div>

          <div className="flex gap-2">
            {["buyer", "seller"].map(r => (
              <button type="button" key={r} data-testid={`reg-role-${r}`}
                onClick={()=>set("role", r)}
                className={`chip flex-1 justify-center ${form.role===r?"active":""}`}>
                {r === "buyer" ? "I'm shopping" : "I'm selling"}
              </button>
            ))}
          </div>
          {err && <div className="text-sm text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2">{err}</div>}
          <button type="submit" data-testid="reg-submit-btn" disabled={busy || !canSubmit} className="btn-primary w-full disabled:opacity-50">
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="text-center text-sm text-[#A1A1AA] mt-6">
          Already have one? <Link to="/login" className="text-[#D4FF00] font-bold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
